import React from 'react';
import { Search, X, Edit2, Trash2 } from 'lucide-react';

/* ───────────── Classes réutilisables (palette neutre : slate + émeraude) ───────────── */

export const inputCls =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm ' +
  'placeholder:text-slate-400 transition focus:border-slate-400 focus:outline-none focus:ring-4 focus:ring-slate-900/5 ' +
  'disabled:bg-slate-50 disabled:text-slate-400 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-slate-500 dark:focus:ring-white/10';

export const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm ' +
  'transition hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-900/20 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200';

export const btnGhost =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 ' +
  'transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-900/5 disabled:cursor-not-allowed disabled:opacity-50 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';

export const iconBtn =
  'rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300 ' +
  'dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white';

export const dangerBtn =
  'rounded-lg p-2 text-slate-500 transition hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-200 ' +
  'dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400';

export const tdCls = 'px-5 py-3.5';
export const rowCls = 'transition hover:bg-slate-50 dark:hover:bg-slate-800/50';

/* ───────────── Formulaires ───────────── */

export const Field = ({ label, hint, span, children }) => (
  <div className={span ? 'sm:col-span-2' : ''}>
    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
    {children}
    {hint && <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
  </div>
);

export const Switch = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus:outline-none focus:ring-4 focus:ring-slate-900/10 ${checked ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

export const Notice = ({ icon: Icon, title, children }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-slate-500 dark:text-slate-400" />
    <div className="text-sm text-slate-600 dark:text-slate-300">
      <p className="mb-0.5 font-semibold text-slate-900 dark:text-white">{title}</p>
      {children}
    </div>
  </div>
);

/* ───────────── Affichage ───────────── */

export const StatCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="text-2xl font-semibold leading-none text-slate-900 dark:text-white">{value}</p>
      <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">{label}</p>
    </div>
  </div>
);

export const IdChip = ({ children }) => (
  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">{children}</span>
);

export const Avatar = ({ icon: Icon, text }) => (
  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-sm font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
    {Icon ? <Icon className="h-5 w-5" /> : (text || '?').charAt(0).toUpperCase()}
  </div>
);

export const StatusBadge = ({ active, on = 'Actif', off = 'Inactif' }) =>
  active ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {on}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> {off}
    </span>
  );

export const RowActions = ({ onEdit, onDelete, children }) => (
  <div className="flex justify-end gap-1">
    <button type="button" onClick={onEdit} className={iconBtn} title="Modifier"><Edit2 className="h-4 w-4" /></button>
    {children}
    <button type="button" onClick={onDelete} className={dangerBtn} title="Supprimer"><Trash2 className="h-4 w-4" /></button>
  </div>
);

export const Table = ({ heads, children }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full">
      <thead>
        <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs font-semibold text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
          {heads.map((h) => (
            <th key={h} className={`px-5 py-3 ${h === 'Actions' ? 'text-right' : ''}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">{children}</tbody>
    </table>
  </div>
);

/* Carte de liste : barre d'outils (compteur + recherche), chargement, état vide */
export const ListCard = ({
  count, singular, plural, search, onSearch, placeholder = 'Rechercher', toolbarExtra,
  loading, hasData, icon: Icon, emptyTitle, emptyText, onCreate, createLabel, createIcon: CreateIcon, children,
}) => (
  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
    <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        <span className="font-semibold text-slate-900 dark:text-white">{count}</span> {count > 1 ? plural : singular}
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {toolbarExtra}
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={placeholder}
            className={`${inputCls} pl-9`}
          />
        </div>
      </div>
    </div>

    {loading ? (
      <div className="space-y-3 p-5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
        ))}
      </div>
    ) : count === 0 ? (
      <div className="flex flex-col items-center px-4 py-16 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800">
          <Icon className="h-6 w-6" />
        </div>
        <p className="font-medium text-slate-900 dark:text-white">{hasData ? 'Aucun résultat' : emptyTitle}</p>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {hasData ? 'Essayez un autre mot-clé.' : emptyText}
        </p>
        {!hasData && onCreate && (
          <button type="button" onClick={onCreate} className={`${btnPrimary} mt-5`}>
            {CreateIcon && <CreateIcon className="h-4 w-4" />} {createLabel}
          </button>
        )}
      </div>
    ) : (
      children
    )}
  </div>
);

/* Modal : en-tête, corps défilant, pied de page. `size="lg"` pour les formulaires à onglets. */
export const Modal = ({ title, subtitle, onClose, onSubmit, confirmLabel, confirmIcon: ConfirmIcon, size = 'md', footerLeft, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
    <div className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700 ${size === 'lg' ? 'max-w-4xl' : 'max-w-md'}`}>
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-800">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h3>
          {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
        </div>
        <button type="button" onClick={onClose} className={iconBtn} aria-label="Fermer"><X className="h-5 w-5" /></button>
      </div>
      <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 overflow-y-auto">{children}</div>
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-900/60">
          <div className="flex gap-2">{footerLeft}</div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className={btnGhost}>Annuler</button>
            <button type="submit" className={btnPrimary}>
              {ConfirmIcon && <ConfirmIcon className="h-4 w-4" />} {confirmLabel}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
);
